import React from 'react';
import { withStyles } from '@material-ui/core/styles';
import {
    arrayOf,
    array,
    bool,
    func,
    number,
    oneOf,
    string
} from 'prop-types';

import { cloneDeep } from 'lodash';

import {
    CustomTreeData,
    TableColumnVisibility,
    TreeDataState
} from '@devexpress/dx-react-grid';
import {
    Grid,
    VirtualTable,
    Table,
    TableHeaderRow,
    TableTreeColumn
} from '@devexpress/dx-react-grid-material-ui';

import InventoryTableContext from './InventoryTableContext';
import InventoryLeafCellFormatter from './InventoryLeafCellFormatter';
import InventoryTableActionCell from './InventoryTableActionCell';
import ProgressBar from '../ProgressBar';

import useTree from '../../hooks/useTree';

import classes from './NiagaraInventoryTable.module.css';

const styles = theme => ({
    tableStriped: {
        backgroundColor:                '#fff',
        '& tbody tr:nth-of-type(even)': { backgroundColor: '#f4f4f4' }}
});

const Columns = [
    { name: 'name', title: 'Name' },
    { name: 'oBIXPath', title: 'OBIX Path' },
    { name: 'objectType', title: 'Type' },
    { name: 'dataType', title: 'Data Type' },
    { name: 'sampleInterval', title: 'Trend Interval' }
];
const LeafOnlyColumns = ['leaf'];

function getRowId({ id }) {
    return id;
}

function getChildRows(row, rootRows) {
    if (!row) {
        return rootRows;
    }
    if (row.items && row.items.length) {
        return row.items;
    }
    return null;
}

function TableCell(cellProps) {
    switch (cellProps.column.name) {
        case 'action':
            return <InventoryTableActionCell {...cellProps} />;
        default:
            return <Table.Cell {...cellProps} />;
    }
}

const TableComponentBase = ({ classes, ...restProps }) => (
    <Table.Table {...restProps} className={classes.tableStriped} />
);
  
export const TableComponent = withStyles(styles, { name: 'TableComponent' })(
    TableComponentBase
);

const NiagaraInventoryTable = ({
    allowFolderSelection,
    defaultSelection,
    editable,
    hiddenColumns,
    inventory,
    loading,
    onCellChange,
    onDeviceSelection,
    rootId,
    treeCacheId,
    ...props
}) => {
    const tree = useTree(inventory, { rootId, treeCacheId });

    const treeClone = cloneDeep(tree);

    const customTree = treeClone.map(item => {
        item.sampleInterval = item.changeOfValue ? 'CoV' : item.sampleInterval;

        return item;
    });

    return (
        <InventoryTableContext.Provider value={{ onCellChange }}>
            <div className={classes.NiagaraInventoryTable}>
                <ProgressBar
                    className={classes.progressBar}
                    hidden={!loading}
                />
                <Grid
                    columns={Columns}
                    getRowId={getRowId}
                    rows={customTree}
                >
                    <InventoryLeafCellFormatter
                        editable={editable}
                        for={LeafOnlyColumns}
                        onCellChange={onCellChange}
                    />
                    <TreeDataState />
                    <CustomTreeData getChildRows={getChildRows} />
                    <VirtualTable cellComponent={TableCell} tableComponent={TableComponent} />
                    <TableHeaderRow />
                    <TableColumnVisibility
                        hiddenColumnNames={hiddenColumns}
                    />
                    <TableTreeColumn
                        for="name"
                    />
                </Grid>
            </div>
        </InventoryTableContext.Provider>
    );
};

NiagaraInventoryTable.defaultProps = {
    allowFolderSelection: true,
    editable:             false,
    defaultSelection:     [],
    hiddenColumns:        [],
    loading:              false,
    onCellChange:         () => {},
    onDeviceSelection:    () => {}
};

NiagaraInventoryTable.propTypes = {
    allowFolderSelection: bool,
    defaultSelection:     arrayOf(string),
    editable:             bool,
    hiddenColumns:        arrayOf(oneOf(Columns.map(({ name }) => name))),
    inventory:            array.isRequired,
    loading:              bool,
    onCellChange:         func,
    onDeviceSelection:    func,
    rootId:               string,
    treeCacheId:          number.isRequired
};

export default NiagaraInventoryTable;
